import React, { useState } from 'react';
import { Button } from '../../../../components/ui/Button';
import Modal from '../../../../components/ui/Modal';
import { useLossReasons } from '../../../settings/loss-reasons/hooks/useLossReasons';
import { ComboboxSelector } from '../../../../components/inputs/ComboboxSelector';

interface LossReasonModalProps {
    isOpen: boolean;
    leadName: string;
    onClose: () => void;
    onConfirm: (reasonId: string, comments?: string) => Promise<void>;
}

export const LossReasonModal: React.FC<LossReasonModalProps> = ({
    isOpen,
    leadName,
    onClose,
    onConfirm
}) => {
    const { reasons, loading, createReason } = useLossReasons();
    const [selectedReasonName, setSelectedReasonName] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);

    // Efeito para limpar seleção ao fechar é controlado pelo componente pai desmontando o modal? 
    // Não, isOpen é prop.
    // UseEffect para resetar? O código original resetava no handleClose.
    // Vamos resetar no modal close (onClose) no pai, ou via useEffect interno.
    // O original resetava em handleClose.

    const handleConfirm = async () => {
        if (!selectedReasonName.trim()) {
            return;
        }

        const selectedReason = reasons.find(r =>
            r.name.toLowerCase() === selectedReasonName.toLowerCase()
        );

        if (!selectedReason) {
            alert('Motivo não encontrado. Aguarde o carregamento e tente novamente.');
            return;
        }

        setIsSaving(true);
        try {
            await onConfirm(selectedReason.id, undefined);
            setSelectedReasonName('');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setSelectedReasonName('');
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="md" className="!overflow-visible">
            <Modal.Header onClose={handleClose}>
                Marcar como perdido
            </Modal.Header>
            <Modal.Body className="space-y-6 !overflow-visible">
                <p className="text-sm text-secondary">
                    Por que a negociação <span className="font-semibold text-default">"{leadName}"</span> foi perdida?
                </p>

                <div>
                    <label className="block text-sm font-medium text-default mb-2">
                        Motivo da perda
                    </label>
                    <ComboboxSelector
                        value={selectedReasonName}
                        onChange={setSelectedReasonName}
                        options={reasons}
                        loading={loading}
                        placeholder="Selecione ou crie um motivo..."
                        createLabel="Criar motivo"
                        onCreate={async (name) => {
                            const newReason = await createReason({ name: name.trim() });
                            return newReason?.name || name.trim();
                        }}
                        disabled={loading}
                    />
                    {reasons.length === 0 && !loading && (
                        <p className="text-xs text-muted mt-2">
                            💡 Nenhum motivo cadastrado. Digite para criar o primeiro!
                        </p>
                    )}
                </div>

                <div className="bg-surface-subtle p-3 rounded-lg border border-subtle">
                    <p className="text-xs text-muted">
                        Gerenciar os motivos de perda na página de configurações da empresa.
                    </p>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline" onClick={handleClose} disabled={isSaving}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={!selectedReasonName || loading || isSaving}
                    variant="danger"
                >
                    {isSaving ? 'Salvando...' : 'Marcar como perdido'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
